import { LevelConfig, LEVELS } from '@/levels/LevelData';
import { PLAYER_SHIP_CLASSES, PLAYER_SHIP_INFO, SHIP_UNLOCK_LEVEL } from '@entities/PlayerShips';
import { MissionOutcome } from '@/fleet/Fleet';
import { ENEMY_INFO, EnemyType } from '@/levels/EnemyTypes';
import { ObjectiveTracker } from '@/levels/ObjectiveTracker';
import { ProgressManager } from '@/GameProgress';

export class LevelManager {
  private currentLevel: LevelConfig | null = null;
  private progressManager: ProgressManager;
  private tracker: ObjectiveTracker | null = null;
  // The last mission's outcome, carried back to the mothership; null once recorded and delivered
  private missionOutcome: MissionOutcome | null = null;
  private missionRecorded = false;

  constructor() {
    this.progressManager = new ProgressManager();
  }

  getProgress(): ProgressManager {
    return this.progressManager;
  }

  renderLevelSelect(): void {
    const levelGrid = document.getElementById('levelGrid');
    if (!levelGrid) return;

    levelGrid.innerHTML = '';
    const levels = this.progressManager.getLevelConfigs();

    levels.forEach(level => {
      const card = document.createElement('div');
      card.className = 'level-card';

      if (level.completed) {
        card.classList.add('completed');
      } else if (level.unlocked) {
        card.classList.add('unlocked');
      } else {
        card.classList.add('locked');
      }

      const statusText = level.completed ? '✓ Completed' : level.unlocked ? 'Play' : 'Locked';

      const deployed = this.progressManager.getFleet().getDeployed();
      const flying = deployed.slice(0, level.deploymentLimit);
      const fleet = flying.length > 0
        ? flying.map(ship => {
          const info = PLAYER_SHIP_INFO[ship.shipClass];
          return this.chip(info.color, ship.name, `${info.name} · hull ${ship.hull}/${ship.maxHull}`, false);
        }).join('')
        : '<span class="roster-empty">No ships deployed. Return to the mothership to choose a squadron.</span>';
      const unlocks = PLAYER_SHIP_CLASSES
        .filter(shipClass => SHIP_UNLOCK_LEVEL[shipClass] === level.id && level.id > 1)
        .map(shipClass => {
          const info = PLAYER_SHIP_INFO[shipClass];
          return this.chip(info.color, `Unlocks ${info.name}`, info.description, true);
        }).join('');

      const roster = (Object.keys(level.enemyWeights) as EnemyType[])
        .filter(type => (level.enemyWeights[type] ?? 0) > 0)
        .map(type => {
          const info = ENEMY_INFO[type];
          return this.chip(info.color, info.name, info.description, false);
        })
        .join('');

      card.innerHTML = `
        <div class="level-card-header">
          <h3><span class="level-number">LEVEL ${level.id}</span>${level.name}</h3>
          <span class="level-status">${statusText}</span>
        </div>
        <p>${level.description}</p>
        <p class="level-objective">${new ObjectiveTracker(level.objective).describe()}</p>
        <div>
          <div class="roster-label">Your squadron · deploys up to ${level.deploymentLimit}</div>
          <div class="chip-row">${fleet}${unlocks}</div>
        </div>
        <div>
          <div class="roster-label">Enemies</div>
          <div class="chip-row">${roster}</div>
        </div>
      `;

      if (level.unlocked) {
        card.onclick = () => this.startLevel(level.id);
      }

      levelGrid.appendChild(card);
    });
  }

  private chip(color: string, label: string, title: string, isNew: boolean): string {
    return `<span class="chip" style="--chip-color: ${color}" title="${title}">${label}${isNew ? '<span class="chip-new">NEW</span>' : ''}</span>`;
  }

  startLevel(levelId: number): void {
    const level = LEVELS.find(l => l.id === levelId);
    if (!level || !this.progressManager.isLevelUnlocked(levelId)) return;

    // The deployed roster ships fly, up to the level's limit; everyone else waits in reserve
    const fleet = this.progressManager.getFleet();
    const deployed = fleet.getDeployed().slice(0, level.deploymentLimit);
    if (deployed.length === 0) {
      this.returnToBase();
      return;
    }
    const reserve = fleet.getShips().filter(ship => !deployed.includes(ship));
    this.missionOutcome = null;
    this.missionRecorded = false;

    // Reset level state
    this.currentLevel = { ...level, objective: { ...level.objective, current: 0 } };
    this.tracker = new ObjectiveTracker(this.currentLevel.objective);
    this.progressManager.setCurrentLevel(levelId);

    // Hide level select and any end-of-level overlays, show game
    this.setDisplay('levelSelect', 'none');
    this.setDisplay('levelComplete', 'none');
    this.setDisplay('gameOver', 'none');
    this.setDisplay('levelInfo', 'block');

    this.updateLevelUI();

    // Trigger game start with level config
    (window as any).startGameWithLevel(this.currentLevel, deployed, reserve);
  }

  /** Restarts the current level from scratch, including objective progress. */
  restartLevel(): void {
    this.startLevel(this.currentLevel?.id ?? 1);
  }

  /** Writes the mission's survivors, losses and pay into the saved roster, once per mission. */
  private recordMission(completed: boolean): MissionOutcome | null {
    if (this.missionRecorded) return this.missionOutcome;
    const report = (window as any).getMissionReport?.(completed);
    if (!report) return null;
    this.missionRecorded = true;
    this.missionOutcome = this.progressManager.recordMission(report);
    return this.missionOutcome;
  }

  private describeOutcome(outcome: MissionOutcome | null): string {
    if (!outcome) return '';
    const lost = outcome.lost.length > 0
      ? `<p class="ships-lost">Lost in action <strong>${outcome.lost.map(s => this.escape(s.name)).join(', ')}</strong></p>`
      : '<p class="ships-safe">No ships lost</p>';
    return `<p class="credits-earned">Credits earned <strong>◆ ${outcome.creditsEarned.toLocaleString('en-GB')}</strong></p>${lost}`;
  }

  /** Called when every deployed ship has been destroyed. */
  onMissionFailed(): void {
    if (!this.currentLevel) return;
    const outcome = this.recordMission(false);
    const report = document.getElementById('gameOverReport');
    if (report) report.innerHTML = this.describeOutcome(outcome);
  }

  updateLevelUI(): void {
    if (!this.currentLevel || !this.tracker) return;

    const levelNum = document.getElementById('currentLevelNum');
    if (levelNum) levelNum.textContent = this.currentLevel.id.toString();

    const levelName = document.getElementById('currentLevelName');
    if (levelName) levelName.textContent = this.currentLevel.name;

    const objective = document.getElementById('levelObjective');
    if (objective) objective.textContent = this.tracker.describe();

    const progress = document.getElementById('objectiveProgress');
    if (progress) progress.textContent = this.tracker.progressText();

    const progressFill = document.getElementById('objectiveProgressFill');
    if (progressFill) progressFill.style.width = `${Math.round(this.tracker.progressFraction() * 100)}%`;
  }

  onEnemyKilled(): void {
    if (!this.currentLevel || !this.tracker) return;

    this.progressManager.addEnemyKill();
    const justCompleted = this.tracker.recordKill();
    this.currentLevel.objective.current = this.tracker.getKills();
    this.updateLevelUI();

    if (justCompleted) this.completeLevel();
  }

  /** Advances survival timers; called once per simulation step. */
  onTick(deltaMs: number): void {
    if (!this.currentLevel || !this.tracker || this.tracker.isComplete()) return;

    const justCompleted = this.tracker.recordTime(deltaMs);
    if (this.currentLevel.objective.type === 'survive') this.updateLevelUI();

    if (justCompleted) this.completeLevel();
  }

  completeLevel(): void {
    if (!this.currentLevel || !this.tracker) return;

    this.progressManager.completeLevel(this.currentLevel.id);

    // Stop the game and bring the survivors' state home
    (window as any).stopGame();
    const outcome = this.recordMission(true);

    // Show level complete screen
    const levelComplete = document.getElementById('levelComplete');
    const levelCompleteStats = document.getElementById('levelCompleteStats');
    const isFinalLevel = this.currentLevel.id === LEVELS[LEVELS.length - 1].id;

    if (levelComplete && levelCompleteStats) {
      const newShips = PLAYER_SHIP_CLASSES
        .filter(shipClass => SHIP_UNLOCK_LEVEL[shipClass] === this.currentLevel!.id + 1)
        .map(shipClass => `<strong>${PLAYER_SHIP_INFO[shipClass].name}</strong>: ${PLAYER_SHIP_INFO[shipClass].description.toLowerCase()}`);

      levelCompleteStats.innerHTML = `
        <p>Level ${this.currentLevel.id} · <strong>${this.currentLevel.name}</strong></p>
        <p>Enemies destroyed <strong>${this.tracker.getKills()}</strong></p>
        ${this.describeOutcome(outcome)}
        ${newShips.length > 0 ? `<p class="unlock-note">New class at the shipyard<br>${newShips.join('<br>')}</p>` : ''}
        ${isFinalLevel ? '<p class="campaign-complete">Campaign complete. The void is yours, Commander.</p>' : ''}
      `;
      levelComplete.style.display = 'block';
    }
  }

  /** Lands on the mothership, delivering the last mission's pay and losses. */
  returnToBase(): void {
    (window as any).stopGame?.();
    this.setDisplay('levelInfo', 'none');
    this.setDisplay('levelComplete', 'none');
    this.setDisplay('gameOver', 'none');
    this.setDisplay('levelSelect', 'none');

    const outcome = this.missionOutcome;
    this.missionOutcome = null;
    (window as any).showBase?.(outcome ? { creditsEarned: outcome.creditsEarned, lost: outcome.lost.map(s => s.name) } : null);
  }

  private escape(text: string): string {
    return text.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
  }

  returnToLevelSelect(): void {
    (window as any).stopGame?.();

    // Hide game UI
    this.setDisplay('levelInfo', 'none');
    this.setDisplay('levelComplete', 'none');
    this.setDisplay('gameOver', 'none');

    // Show level select
    this.setDisplay('levelSelect', 'flex');

    this.renderLevelSelect();
  }

  getCurrentLevel(): LevelConfig | null {
    return this.currentLevel;
  }

  resetProgress(): void {
    this.progressManager.resetProgress();
    this.missionOutcome = null;
    this.returnToBase();
  }

  private setDisplay(elementId: string, display: string): void {
    const element = document.getElementById(elementId);
    if (element) element.style.display = display;
  }
}

// Global functions for HTML
(window as any).levelManager = new LevelManager();
(window as any).returnToLevelSelect = () => (window as any).levelManager.returnToLevelSelect();
(window as any).returnToBase = () => (window as any).levelManager.returnToBase();
(window as any).resetAllProgress = () => (window as any).levelManager.resetProgress();
